import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { 
  DollarSign, ShoppingCart, TrendingUp, TrendingDown, 
  FileText, Download, Printer, Search, Filter, 
  Package, Briefcase, CreditCard, Users 
} from 'lucide-react';

import ReportLayout from '../components/ReportLayout';
import { Link } from 'react-router-dom';

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-KE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const currency = (v) => `KES ${Number(v || 0).toLocaleString()}`;

const escapeCsv = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toCsv = (rows, headers) => {
  const headerLine = headers.map((h) => escapeCsv(h.label)).join(',');
  const dataLines = rows.map((r) => headers.map((h) => escapeCsv(h.value(r))).join(','));
  return `${headerLine}\n${dataLines}`;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Sparkline-like bar chart for trend
const TrendBarChart = ({ series = [], height = 200 }) => {
  const values = series.map((s) => s.value);
  const maxV = Math.max(1, ...values);

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-end gap-1.5" style={{ minWidth: Math.max(series.length * 30, 300), height }}>
        {series.map((p, idx) => {
          const h = (Number(p.value || 0) / maxV) * (height - 40);
          const barH = Math.max(h, 4);
          return (
            <div key={`${p.label}-${idx}`} className="flex-1 flex flex-col justify-end items-center">
              <div className="w-full bg-blue-500 rounded-t-md hover:bg-blue-600 transition-colors" style={{ height: barH }} title={currency(p.value)} />
              <div className="text-[9px] text-gray-500 mt-1.5 truncate w-full text-center" title={p.label}>
                {p.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Donut chart SVG component
const DonutChart = ({ slices = [], size = 220 }) => {
  const total = slices.reduce((s, x) => s + Number(x.total || 0), 0) || 1;
  const colors = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#0ea5e9', '#111827', '#ec4899', '#14b8a6', '#f97316'];
  const radius = 45;
  const innerRadius = 30;
  const circumference = 2 * Math.PI * radius;
  let acc = 0;

  const labelPos = (frac, midFrac) => {
    const midAngle = (frac - midFrac / 2) * 360 - 90;
    const rad = (midAngle * Math.PI) / 180;
    const labelRadius = radius + 16;
    return {
      x: 70 + labelRadius * Math.cos(rad),
      y: 70 + labelRadius * Math.sin(rad),
    };
  };

  return (
    <div className="flex items-center justify-center">
      <svg width={size} height={size * 0.85} viewBox="0 0 180 160">
        <circle cx="70" cy="75" r={radius} stroke="#E5E7EB" strokeWidth="12" fill="none" />
        {slices.map((s, i) => {
          const value = Number(s.total || 0);
          const pct = value / total;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const midFrac = pct;
          const pos = labelPos(acc + pct, midFrac);
          const labelPct = (pct * 100).toFixed(1);
          const showLabel = pct > 0.05;
          const offset = -acc * circumference;
          acc += pct;

          return (
            <g key={s.label || i}>
              <circle
                cx="70"
                cy="75"
                r={radius}
                fill="none"
                stroke={colors[i % colors.length]}
                strokeWidth="12"
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 70 75)"
              />
              {showLabel && (
                <>
                  <text x={pos.x} y={pos.y - 4} textAnchor="middle" fontSize="7" fill={colors[i % colors.length]} fontWeight="600">
                    {s.label}
                  </text>
                  <text x={pos.x} y={pos.y + 8} textAnchor="middle" fontSize="7" fill="#6B7280">
                    {labelPct}%
                  </text>
                </>
              )}
            </g>
          );
        })}
        <text x="70" y="71" textAnchor="middle" dominantBaseline="central" className="fill-gray-800" fontSize="8">Total</text>
        <text x="70" y="85" textAnchor="middle" className="fill-gray-900" fontSize="11" fontWeight="700">{currency(total)}</text>
      </svg>
    </div>
  );
};

// Payment method chart
const PaymentMethodChart = ({ data = [] }) => {
  const slices = data.map(d => ({ label: d.payment_method, total: d.total_revenue }));
  return (
    <div>
      <DonutChart slices={slices} size={200} />
      <div className="mt-3 space-y-1.5">
        {data.map((item, idx) => (
          <div key={item.payment_method} className="flex items-center justify-between text-sm px-2">
            <span className="text-gray-600 capitalize">{item.payment_method}</span>
            <span className="font-semibold text-gray-800">{currency(item.total_revenue)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const colors = {
    completed: 'bg-green-50 text-green-800 border-green-100',
    partial: 'bg-yellow-50 text-yellow-800 border-yellow-100',
    pending: 'bg-orange-50 text-orange-800 border-orange-100',
    refunded: 'bg-red-50 text-red-800 border-red-100',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${colors[status] || 'bg-gray-50 text-gray-800 border-gray-100'}`}>
      {status || 'unknown'}
    </span>
  );
};

const ReportsSales = () => {
  const getCurrentMonthRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const toISODate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { start: toISODate(start), end: toISODate(end) };
  }, []);
  const [dateRange, setDateRange] = useState({ start: getCurrentMonthRange.start, end: getCurrentMonthRange.end });
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatus, setActiveStatus] = useState('all');
  const [errorOnce, setErrorOnce] = useState(false);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await api.get('/reports/sales-report', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      setReportData(res.data);
      setErrorOnce(false);
    } catch (e) {
      if (!errorOnce) {
        toast.error('Failed to load sales report');
        setErrorOnce(true);
      }
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const summary = reportData?.summary || {};
  const salesTrend = reportData?.sales_trend || [];
  const revenueByServiceCategory = reportData?.revenue_by_service_category || [];
  const topProducts = reportData?.top_products || [];
  const topNutritionists = reportData?.top_nutritionists || [];
  const salesByPaymentMethod = reportData?.sales_by_payment_method || [];
  const salesByBranch = reportData?.sales_by_branch || [];
  const detailedSales = reportData?.detailed_sales || [];

  // Filter detailed sales table
  const filteredSales = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return detailedSales.filter((s) => {
      if (activeStatus !== 'all' && s.payment_status !== activeStatus) return false;
      if (!q) return true;
      return (
        String(s.receipt_number || '').toLowerCase().includes(q) ||
        String(s.client_name || '').toLowerCase().includes(q) ||
        String(s.payment_method || '').toLowerCase().includes(q) ||
        String(s.cashier_name || '').toLowerCase().includes(q)
      );
    });
  }, [detailedSales, searchTerm, activeStatus]);

  // Trend series for chart
  const trendSeries = salesTrend.map((r) => ({
    label: formatDate(r.date).split(',')[0] || r.date?.slice(5),
    value: r.total_sales,
  }));

  // KPI definitions
  const topKpis = [
    {
      label: 'Total Sales',
      value: currency(summary.total_sales),
      icon: <DollarSign size={18} className="text-green-700" />,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-700',
    },
    {
      label: 'Total Transactions',
      value: (summary.total_transactions || 0).toLocaleString(),
      icon: <ShoppingCart size={18} className="text-blue-700" />,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
    },
    {
      label: 'Consultation Revenue',
      value: currency(summary.consultation_revenue),
      icon: <Briefcase size={18} className="text-purple-700" />,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-700',
    },
    {
      label: 'Product Revenue',
      value: currency(summary.product_revenue),
      icon: <Package size={18} className="text-orange-700" />,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-700',
    },
    {
      label: 'Average Sale Value',
      value: currency(summary.avg_sale_value),
      icon: <TrendingUp size={18} className="text-cyan-700" />,
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-700',
    },
  ];

  // Export CSV
  const exportCsv = () => {
    const headers = [
      { label: 'Receipt #', value: (r) => r.receipt_number },
      { label: 'Date', value: (r) => r.sale_date },
      { label: 'Client', value: (r) => r.client_name || 'Walk-in' },
      { label: 'Cashier', value: (r) => r.cashier_name },
      { label: 'Payment Method', value: (r) => r.payment_method },
      { label: 'Status', value: (r) => r.payment_status },
      { label: 'Subtotal (KES)', value: (r) => r.subtotal },
      { label: 'Discount (KES)', value: (r) => r.discount },
      { label: 'Tax (KES)', value: (r) => r.tax },
      { label: 'Total (KES)', value: (r) => r.total_amount },
      { label: 'Paid (KES)', value: (r) => r.amount_paid },
      { label: 'Balance (KES)', value: (r) => r.balance_due },
    ];
    const csv = toCsv(filteredSales, headers);
    downloadCsv(`sales_report_${dateRange.start}_to_${dateRange.end}.csv`, csv);
  };

  // Left slot: charts and key breakdowns
  const leftSlot = (
    <div className="space-y-6">
      {/* Sales Trend Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-hidden">
        <p className="font-semibold text-gray-800 mb-3">Daily Sales Trend</p>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : salesTrend.length > 0 ? (
          <TrendBarChart series={trendSeries} height={200} />
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No sales data for selected period.</div>
        )}
      </div>

      {/* Revenue by Service Category */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-hidden">
        <p className="font-semibold text-gray-800 mb-3">Revenue by Service Category</p>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : revenueByServiceCategory.length > 0 ? (
          <div>
            <DonutChart slices={revenueByServiceCategory.map(r => ({ label: r.category, total: r.total_revenue }))} size={200} />
            <div className="mt-3 space-y-1">
              {revenueByServiceCategory.map((item, idx) => (
                <div key={item.category} className="flex items-center justify-between text-sm px-2">
                  <span className="text-gray-600 capitalize">{item.category.replace(/_/g, ' ')}</span>
                  <span className="font-semibold text-gray-800">{currency(item.total_revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No service revenue data.</div>
        )}
      </div>

      {/* Sales by Payment Method */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-hidden">
        <p className="font-semibold text-gray-800 mb-3">Sales by Payment Method</p>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : salesByPaymentMethod.length > 0 ? (
          <PaymentMethodChart data={salesByPaymentMethod} />
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No payment data.</div>
        )}
      </div>
    </div>
  );

  // Right slot: tables and lists
  const rightSlot = (
    <div className="space-y-6">
      {/* Top Selling Products */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Package size={18} className="text-orange-600" />
          <p className="font-semibold text-gray-800">Top Selling Products</p>
        </div>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : topProducts.length > 0 ? (
<div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Product</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Qty Sold</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {topProducts.slice(0, 10).map((p, idx) => (
                  <tr key={p.sku || idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-sm text-gray-800">{p.name}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-semibold text-gray-700">{p.total_quantity}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-semibold text-green-700">{currency(p.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No product sales data.</div>
        )}
      </div>

      {/* Best Performing Nutritionists */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Users size={18} className="text-purple-600" />
          <p className="font-semibold text-gray-800">Best Performing Staff</p>
        </div>
{loading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
        ) : topNutritionists.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Transactions</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Service Rev</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Total Rev</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {topNutritionists.map((n, idx) => (
                  <tr key={n.id || idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-sm text-gray-800 font-medium">{n.full_name}</td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-700">{n.total_transactions}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-semibold text-purple-700">{currency(n.service_revenue)}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-semibold text-green-700">{currency(n.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No staff performance data.</div>
        )}
      </div>

      {/* Sales by Branch (placeholder) */}
      {salesByBranch.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-hidden">
          <p className="font-semibold text-gray-800 mb-3">Sales by Branch</p>
          <div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Branch</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Transactions</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {salesByBranch.map((b, idx) => (
                  <tr key={b.branch || idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5 text-sm text-gray-800">{b.branch}</td>
                    <td className="px-3 py-2.5 text-sm text-right text-gray-700">{b.transaction_count}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-semibold text-green-700">{currency(b.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Detailed sales transaction table
  const tablesSlot = (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-gray-800">Detailed Sales Transactions</p>
            <p className="text-sm text-gray-500 mt-0.5">{filteredSales.length} transactions found</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-200 bg-white"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="partial">Partial</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
            </select>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search transactions..."
                className="w-56 pl-9 pr-3 py-1.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" />
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="px-6 py-16 text-center text-gray-500">
            No transactions found for selected period.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full report-table">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Receipt #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Cashier</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSales.map((s) => (
                  <tr key={s.receipt_number || s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.receipt_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(s.sale_date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[140px] truncate" title={s.client_name || ''}>
                      {s.client_name || 'Walk-in'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s.cashier_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{s.payment_method || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.payment_status} /></td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      {Array.isArray(s.items) ? s.items.length : 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">{currency(s.total_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-blue-700">{currency(s.amount_paid)}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">
                      {s.balance_due > 0 ? (
                        <span className="text-red-600">{currency(s.balance_due)}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

return (
    <ReportLayout
      title="Sales Report"
      description="Measure sales performance – track revenue, transactions, and top performers"
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      onExportCsv={exportCsv}
      onPrint={() => window.print()}
      printLink="/reports/sales/print"
      kpis={topKpis}
      leftSlot={leftSlot}
      rightSlot={rightSlot}
      tablesSlot={tablesSlot}
    />
  );
};

export default ReportsSales;